import { QueryClient } from "@tanstack/react-query";
import { getAuthToken } from "./authAdmin";

export const BACKEND = process.env.REACT_APP_BACKEND_URL || "https://api.resto-online.be";
//const BACKEND = "http://localhost:6969";
export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
        },
    },
});
export async function loginAdmin({ email, password, rememberMe }) {
    const response = await fetch(`${BACKEND}/admin/auth/login`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password, rememberMe }),
    });


    if (!response.ok) {
        const error = new Error("Er is iets misgegaan tijdens het inloggen.");
        error.code = response.status;
        error.info = await response.json();

        throw error;
    }

    return await response.json();
}

export async function signupAdmin({ email, password, name }) {
    const response = await fetch(`${BACKEND}/admin/auth/signup`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password, name }),
    });

    if (!response.ok) {
        const error = new Error("Er is iets misgegaan tijdens het registreren.");
        error.code = response.status;
        error.info = await response.json();

        throw error;
    }

    return await response.json();
}
export const addProduct = async (productData) => {
    const token = getAuthToken();

    try {
        console.log(`Sending POST request to ${BACKEND}/admin/products`);
        
        // Set proper headers for multipart/form-data
        const headers = {
            Authorization: `Bearer ${token}`
        };
        
        // Create fetch options
        const options = {
            method: "POST",
            headers,
            body: productData,
        };
        
        // Execute the request with timeout of 60 seconds
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000);
        
        const response = await fetch(`${BACKEND}/admin/products`, {
            ...options,
            signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        // Log response details for debugging
        console.log(`Response status: ${response.status} (${response.statusText})`);
        
        if (!response.ok) {
            // Special handling for 413 Request Entity Too Large
            if (response.status === 413) {
                throw new Error(
                    "De afbeelding is te groot. Probeer een kleinere of meer gecomprimeerde afbeelding te uploaden."
                );
            }
            
            const errorData = await response.json().catch(() => ({}));
            console.error("Error response data:", errorData);
            
            const error = new Error(
                errorData.message || "Er is iets misgegaan tijdens het toevoegen van een product."
            );
            error.code = response.status;
            error.info = errorData;
            throw error;
        }
        
        return await response.json();
    } catch (error) {
        console.error("Error in addProduct:", error);
        if (error.name === 'AbortError') {
            throw new Error("De aanvraag heeft te lang geduurd en is afgebroken. Probeer een kleinere afbeelding.");
        }
        throw error;
    }
};

export const updateProduct = async (productData) => {
    const token = getAuthToken();

    // Extract ID from FormData or object
    let id;
    if (productData instanceof FormData) {
        id = productData.get('_id');
        // Remove _id from FormData to avoid duplicate in URL
        productData.delete('_id');
    } else {
        id = productData._id;
    }

    if (!id) {
        throw new Error("Product ID ontbreekt. Kan geen update uitvoeren.");
    }

    try {
        console.log(`Sending PUT request to ${BACKEND}/admin/products/${id}`);
        
        // Set proper headers for multipart/form-data
        const headers = {
            Authorization: `Bearer ${token}`
        };
        
        // Create fetch options
        const options = {
            method: "PUT",
            headers,
            body: productData,
        };
        
        // Execute the request with timeout of 60 seconds
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000);
        
        const response = await fetch(`${BACKEND}/admin/products/${id}`, {
            ...options,
            signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        // Log response details for debugging
        console.log(`Response status: ${response.status} (${response.statusText})`);
        
        if (!response.ok) {
            // Special handling for 413 Request Entity Too Large
            if (response.status === 413) {
                throw new Error(
                    "De afbeelding is te groot. Probeer een kleinere of meer gecomprimeerde afbeelding te uploaden."
                );
            }
            
            const errorData = await response.json().catch(() => ({}));
            console.error("Error response data:", errorData);
            
            const error = new Error(
                errorData.message || "Er is iets misgegaan tijdens het bijwerken van het product."
            );
            error.code = response.status;
            error.info = errorData;
            throw error;
        }
        
        return await response.json();
    } catch (error) {
        console.error("Error in updateProduct:", error);
        if (error.name === 'AbortError') {
            throw new Error("De aanvraag heeft te lang geduurd en is afgebroken. Probeer een kleinere afbeelding.");
        }
        throw error;
    }
};

export const deleteProduct = async (productId) => {
    const token = getAuthToken(); // Verkrijg de authenticatietoken.

    if (!productId) {
        throw new Error("Product ID ontbreekt. Kan geen verwijdering uitvoeren.");
    }

    const response = await fetch(
        `${BACKEND}/admin/products/${productId}`, {
        method: "DELETE",
        headers: {
            Authorization: `Bearer ${token}`, // Voeg de token toe aan de Authorization-header.
        },
    }
    );

    if (!response.ok) {
        const error = new Error(
            "Er is iets misgegaan tijdens het verwijderen van het product."
        );
        error.code = response.status;
        error.info = await response.json();

        throw error;
    }

    return response.json();
};

export const fetchProducts = async ({ signal }) => {
    const token = getAuthToken(); // Verkrijg de authenticatietoken.
    const response = await fetch(`${BACKEND}/admin/products`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });

    if (!response.ok) {
        const error = new Error(
            "Er is iets misgegaan tijdens het ophalen van de producten."
        );
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const fetchCollections = async ({ signal }) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/collections`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });

    if (!response.ok) {
        const error = new Error("Er is iets misgegaan tijdens het ophalen van de collecties.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const addMenu = async (menuData) => {
    const token = getAuthToken(); // Verkrijg de authenticatietoken.

    const response = await fetch(`${BACKEND}/admin/menus`, {
        method: "POST",
        headers: {
            // "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, // Voeg de token toe aan de Authorization-header.
        },
        body: menuData,
    });

    if (!response.ok) {
        const error = new Error(
            "Er is iets misgegaan tijdens het toevoegen van een menu."
        );
        error.code = response.status;
        error.info = await response.json();

        throw error;
    }

    return response.json();
};

export const updateMenu = async (menuData) => {
    const token = getAuthToken(); // Verkrijg de authenticatietoken.

    // Haal het ID uit de FormData of object
    const id = menuData instanceof FormData ? menuData.get('_id') : menuData._id;

    if (!id) {
        throw new Error("Menu ID ontbreekt. Kan geen update uitvoeren.");
    }

    const response = await fetch(
        `${BACKEND}/admin/menus/${id}`, {
        method: "PUT",
        headers: {
            Authorization: `Bearer ${token}`, // Voeg de token toe aan de Authorization-header.
        },
        body: menuData,
    }
    );

    if (!response.ok) {
        const error = new Error(
            "Er is iets misgegaan tijdens het bijwerken van het menu."
        );
        error.code = response.status;
        error.info = await response.json();

        throw error;
    }

    return response.json();
};

export const deleteMenu = async (menuId) => {
    const token = getAuthToken(); // Verkrijg de authenticatietoken.

    if (!menuId) {
        throw new Error("Menu ID ontbreekt. Kan geen verwijdering uitvoeren.");
    }

    const response = await fetch(`${BACKEND}/admin/menus/${menuId}`, {
        method: "DELETE",
        headers: {
            Authorization: `Bearer ${token}`, // Voeg de token toe aan de Authorization-header.
        },
    });

    if (!response.ok) {
        const error = new Error(
            "Er is iets misgegaan tijdens het verwijderen van het menu."
        );
        error.code = response.status;
        error.info = await response.json();

        throw error;
    }

    return response.json();
};

export const fetchMenus = async ({ signal }) => {
    const token = getAuthToken(); // Verkrijg de authenticatietoken.
    const response = await fetch(`${BACKEND}/admin/menus`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });

    if (!response.ok) {
        const error = new Error(
            "Er is iets misgegaan tijdens het ophalen van de menu's."
        );
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const fetchAccountSettings = async () => {
    const token = getAuthToken();
    const response = await fetch(
        `${BACKEND}/admin/account/settings`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
    }
    );

    if (!response.ok) {
        const error = new Error(
            "Er is iets misgegaan tijdens het ophalen van de accountinstellingen."
        );
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

// ===== Abonnement (Stripe) =====
export const fetchSubscription = async () => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/subscription`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
        const error = new Error("Kon abonnementsgegevens niet ophalen.");
        error.code = response.status;
        error.info = await response.json().catch(() => ({}));
        throw error;
    }
    return response.json();
};

// Start Stripe Checkout voor een plan (+ optionele cadeaubonnen-add-on). Geeft de checkout-URL terug.
export const createSubscriptionCheckout = async ({ plan, giftCards }) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/subscription/checkout`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plan, giftCards }),
    });
    if (!response.ok) {
        const error = new Error("Kon de betaling niet starten.");
        error.code = response.status;
        error.info = await response.json().catch(() => ({}));
        throw error;
    }
    return response.json(); // { url }
};

// Wijzig een bestaand abonnement ter plekke (plan wisselen / cadeaubonnen toevoegen of verwijderen).
// Alleen de meegegeven velden worden gewijzigd; de rest blijft ongewijzigd. Geeft de nieuwe staat terug.
export const changeSubscription = async ({ plan, giftCards } = {}) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/subscription/change`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plan, giftCards }),
    });
    if (!response.ok) {
        const error = new Error("Kon het abonnement niet wijzigen.");
        error.code = response.status;
        error.info = await response.json().catch(() => ({}));
        throw error;
    }
    return response.json(); // { plan, addons, subscriptionStatus, hasSubscription }
};

// Open het Stripe Billing Portal om het abonnement te beheren/opzeggen. Geeft de portal-URL terug.
export const createSubscriptionPortal = async () => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/subscription/portal`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
        const error = new Error("Kon het beheerportaal niet openen.");
        error.code = response.status;
        error.info = await response.json().catch(() => ({}));
        throw error;
    }
    return response.json(); // { url }
};

export const updateAccountSettings = async (settingsData) => {
    const token = getAuthToken();

    const response = await fetch(
        `${BACKEND}/admin/account/settings`, {
        method: "PUT",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        body: settingsData,
    }
    );

    if (!response.ok) {
        const error = new Error(
            "Er is iets misgegaan tijdens het bijwerken van de accountinstellingen."
        );
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

// Maximum aantal bestellingen per ophaalmoment instellen (null = onbeperkt)
export const updatePickupCapacity = async (maxOrdersPerSlot) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/pickup-moments/capacity`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ maxOrdersPerSlot }),
    });

    if (!response.ok) {
        const info = await response.json().catch(() => ({}));
        const error = new Error(info.message || "Er is iets misgegaan tijdens het opslaan van het maximum.");
        error.code = response.status;
        error.info = info;
        throw error;
    }

    return response.json();
};

export const fetchPickupMoments = async ({ signal }) => {
    const token = getAuthToken(); // Verkrijg de authenticatietoken.

    const response = await fetch(`${BACKEND}/admin/pickup-moments`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`, // Voeg de token toe aan de Authorization-header.
        },
        signal, // Optioneel: Voeg de signal toe voor het annuleren van requests.
    });

    if (!response.ok) {
        const error = new Error(
            "Er is iets misgegaan tijdens het ophalen van de ophaalmomenten."
        );
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json(); // Geef de JSON-response terug.
};

export const updatePickupSlots = async (slots, day) => {
    const token = getAuthToken(); // Verkrijg de authenticatietoken.

    if (!slots) {
        throw new Error("Geen pickup slot data ontvangen.");
    }

    // Add the day to the data being sent
    const dataToSend = { day, slots }; // Include the day in the data object.
    const response = await fetch(`${BACKEND}/admin/pickup-moments`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, // Voeg de token toe aan de Authorization-header.
        },
        body: JSON.stringify(dataToSend),
    });



    if (!response.ok) {
        const error = new Error(
            "Er is iets misgegaan tijdens het bijwerken van de pickup slots."
        );
        error.code = response.status;
        error.info = await response.json();

        console.log(error.info);

        throw error;
    }

    return response.json(); // Return de response als het bijwerken succesvol is.
};

export const upsertSpecialPickupSlots = async ({ date, slots }) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/pickup-moments/special`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ date, slots }),
    });
    if (!response.ok) {
        const error = new Error("Er is iets misgegaan tijdens het bijwerken van de speciale pickup slots.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }
    return response.json();
};

export const deleteSpecialPickupSlot = async (date) => {
    const token = getAuthToken();
    const params = new URLSearchParams({ date }).toString();
    const response = await fetch(`${BACKEND}/admin/pickup-moments/special?${params}`, {
        method: "DELETE",
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    if (!response.ok) {
        const error = new Error("Er is iets misgegaan tijdens het verwijderen van de speciale datum.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }
    return response.json();
};

export const fetchOpeningHours = async ({ signal }) => {
    const token = getAuthToken(); // Verkrijg de authenticatietoken.

    const response = await fetch(`${BACKEND}/admin/opening-hours`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`, // Voeg de token toe aan de Authorization-header.
        },
        signal, // Optioneel: Voeg de signal toe voor het annuleren van requests.
    });

    if (!response.ok) {
        const error = new Error(
            "Er is iets misgegaan tijdens het ophalen van de openingsuren."
        );
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json(); // Geef de JSON-response terug.
};

export const updateOpeningHours = async (slots, day) => {
    const token = getAuthToken(); // Verkrijg de authenticatietoken.

    if (!slots) {
        throw new Error("Geen pickup slot data ontvangen.");
    }

    // Add the day to the data being sent
    const dataToSend = { day, slots }; // Include the day in the data object.
    const response = await fetch(`${BACKEND}/admin/opening-hours`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, // Voeg de token toe aan de Authorization-header.
        },
        body: JSON.stringify(dataToSend),
    });

    if (!response.ok) {
        const error = new Error(
            "Er is iets misgegaan tijdens het bijwerken van de openingsuren."
        );
        error.code = response.status;
        error.info = await response.json();

        throw error;
    }

    return response.json(); // Return de response als het bijwerken succesvol is.
};

// Uitzonderlijke dag voor reservaties/openingsuren (date = "YYYY-MM-DD")
export const upsertSpecialOpeningHours = async ({ date, slots }) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/opening-hours/special`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ date, slots }),
    });
    if (!response.ok) {
        const error = new Error("Er is iets misgegaan tijdens het bijwerken van de uitzonderlijke dag.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }
    return response.json();
};

export const deleteSpecialOpeningHours = async (date) => {
    const token = getAuthToken();
    const params = new URLSearchParams({ date }).toString();
    const response = await fetch(`${BACKEND}/admin/opening-hours/special?${params}`, {
        method: "DELETE",
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    if (!response.ok) {
        const error = new Error("Er is iets misgegaan tijdens het verwijderen van de uitzonderlijke dag.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }
    return response.json();
};

export const updateVacationMode = async (vacationData) => {
    const token = getAuthToken(); // Verkrijg de authenticatietoken.


    const response = await fetch(
        `${BACKEND}/admin/vacation-modes/${vacationData._id}`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, // Voeg de token toe aan de Authorization-header.
        },
        body: JSON.stringify(vacationData),
    }
    );

    if (!response.ok) {
        const error = new Error(
            "Er is iets misgegaan tijdens het bijwerken van de vakantie modus."
        );
        error.code = response.status;
        error.info = await response.json();

        throw error;
    }

    return response.json();
};

export const deleteVacationMode = async (vacationModeId) => {
    const token = getAuthToken(); // Verkrijg de authenticatietoken.

    if (!vacationModeId) {
        throw new Error("Vakantiemodus ID ontbreekt. Kan geen verwijdering uitvoeren.");
    }

    const response = await fetch(
        `${BACKEND}/admin/vacation-modes/${vacationModeId}`, {
        method: "DELETE",
        headers: {
            Authorization: `Bearer ${token}`, // Voeg de token toe aan de Authorization-header.
        },
    }
    );

    if (!response.ok) {
        const error = new Error(
            "Er is iets misgegaan tijdens het verwijderen van de vakantiemodus."
        );
        error.code = response.status;
        error.info = await response.json();

        throw error;
    }

    return response.json(); // Return de serverresponse als de verwijdering succesvol is.
};


export const fetchVacations = async () => {
    const token = getAuthToken(); // Verkrijg de authenticatietoken.

    const response = await fetch(
        `${BACKEND}/admin/vacation-modes`, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, // Voeg de token toe aan de Authorization-header.
        },
    }
    );

    if (!response.ok) {
        const error = new Error(
            "Er is iets misgegaan tijdens het ophalen van de vakantiemodus."
        );
        error.code = response.status;
        error.info = await response.json();

        throw error;
    }

    return response.json();
};

// Voeg een nieuwe vakantieperiode toe
export const addVacationMode = async (vacationData) => {
    const token = getAuthToken(); // Verkrijg de authenticatietoken.

    const response = await fetch(
        `${BACKEND}/admin/vacation-modes`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, // Voeg de token toe aan de Authorization-header.
        },
        body: JSON.stringify(vacationData),
    }
    );

    if (!response.ok) {
        const error = new Error(
            "Er is iets misgegaan tijdens het toevoegen van de vakantiemodus."
        );
        error.code = response.status;
        error.info = await response.json();

        throw error;
    }

    return response.json();
};

export const addSubdomain = async ({ subdomain }) => {
    const token = getAuthToken(); // Verkrijg de authenticatietoken.

    if (!subdomain) {
        throw new Error("Subdomein ontbreekt. Kan de webshop niet lanceren.");
    }

    const response = await fetch(`${BACKEND}/admin/stripe-connect/add-subdomain`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ subdomain }),
    });

    if (!response.ok) {
        const error = new Error("Er is iets misgegaan tijdens het lanceren van de webshop.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const fetchOnboardingLink = async ({ signal }) => {
    const token = getAuthToken(); // Verkrijg de authenticatietoken.
    const response = await fetch(`${BACKEND}/admin/stripe-connect/onboarding-link`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });

    if (!response.ok) {


        const error = new Error(
            "Er is iets misgegaan tijdens het ophalen van de onboardinglink."
        );
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const fetchOrdersToday = async ({ signal }) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/orders/today`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });

    if (!response.ok) {
        const error = new Error("Fout bij het ophalen van bestellingen voor vandaag.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const fetchOrdersFuture = async ({ signal }) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/orders/future`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });

    if (!response.ok) {
        const error = new Error("Fout bij het ophalen van toekomstige bestellingen.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const fetchOrdersPast = async ({ signal }) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/orders/past`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });    
    if (!response.ok) {
        const error = new Error("Fout bij het ophalen van voorbije bestellingen.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const markOrderAsPickedUp = async (orderId) => {


    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/orders/${orderId}/mark-picked-up`, {
        method: "PATCH",
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ markedAsPickedUp: true }),
    });

    if (!response.ok) {
        const error = new Error("Fout bij het markeren van een bestelling als opgehaald.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const fetchOrdersSummary = async ({ from, to, signal }) => {
    const token = getAuthToken();
    const params = new URLSearchParams({ from, to }).toString();
    const response = await fetch(`${BACKEND}/admin/orders/summary?${params}`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });

    if (!response.ok) {
        const error = new Error("Fout bij het ophalen van het order-overzicht.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const fetchOrdersSummaryDetails = async ({ from, to, key, signal }) => {
    const token = getAuthToken();
    const params = new URLSearchParams({ from, to }).toString();
    const encodedKey = encodeURIComponent(key);
    const response = await fetch(`${BACKEND}/admin/orders/summary/${encodedKey}/details?${params}`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });

    if (!response.ok) {
        const error = new Error("Fout bij het ophalen van item details.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const fetchOrderById = async ({ id, signal } = {}) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/orders/${id}`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });

    if (!response.ok) {
        const error = new Error("Fout bij het ophalen van bestelling.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const fetchAllOrders = async ({ from, to, signal, includeGiftCards = false } = {}) => {
    const token = getAuthToken();
    const params = new URLSearchParams();
    if (from && to) {
        params.append("from", from);
        params.append("to", to);
    }
    if (includeGiftCards) params.append("includeGiftCards", "true");

    const url = params.toString()
        ? `${BACKEND}/admin/orders?${params.toString()}`
        : `${BACKEND}/admin/orders`;

    const response = await fetch(url, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });

    if (!response.ok) {
        const error = new Error("Fout bij het ophalen van alle bestellingen.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const deleteOrder = async (orderId) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/orders/${orderId}`, {
        method: "DELETE",
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        const error = new Error("Fout bij het verwijderen van de bestelling.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const addDiscount = async (discountData) => {
    const token = getAuthToken(); // Haal de authenticatietoken op

    const response = await fetch(`${BACKEND}/admin/discounts`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, // Voeg de token toe
        },
        body: JSON.stringify(discountData),
    });

    if (!response.ok) {
        const error = new Error("Er is iets misgegaan tijdens het toevoegen van de korting.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const updateDiscount = async (discountData) => {
    const token = getAuthToken(); // Haal de authenticatietoken op

    const response = await fetch(`${BACKEND}/admin/discounts/${discountData._id}`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, // Voeg de token toe
        },
        body: JSON.stringify(discountData),
    });

    if (!response.ok) {
        const error = new Error("Er is iets misgegaan tijdens het bijwerken van de korting.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};
export const fetchDiscounts = async ({ signal } = {}) => {
    const token = getAuthToken(); // Haal de authenticatietoken op

    const response = await fetch(`${BACKEND}/admin/discounts`, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        signal,
    });

    if (!response.ok) {
        const error = new Error("Er is iets misgegaan bij het ophalen van de kortingen.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const deleteDiscount = async (discountId) => {
    const token = getAuthToken(); // Haal de authenticatietoken op

    const response = await fetch(`${BACKEND}/admin/discounts/${discountId}`, {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        const error = new Error("Er is iets misgegaan bij het verwijderen van de korting.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

// Gift cards admin
export const fetchGiftCards = async ({ signal } = {}) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/gift-cards`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
        signal,
    });
    if (!response.ok) {
        const error = new Error("Fout bij het ophalen van cadeaubonnen.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }
    return response.json();
};

export const createGiftCard = async (data) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/gift-cards`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        const error = new Error("Fout bij het aanmaken van de cadeaubon.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }
    return response.json();
};

export const deleteGiftCard = async (id) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/gift-cards/${id}`, {
        method: "DELETE",
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    if (!response.ok) {
        const error = new Error("Fout bij het verwijderen van de cadeaubon.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }
    return response.json();
};
// Collection functies
export const addCollection = async (data) => {
    const isFormData = (typeof FormData !== 'undefined') && (data instanceof FormData);
    const response = await fetch(`${BACKEND}/admin/collections`, {
        method: "POST",
        headers: isFormData ? {
            Authorization: `Bearer ${getAuthToken()}`,
        } : {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
        },
        body: isFormData ? data : JSON.stringify(data),
    });

    const responseData = await response.json();

    if (!response.ok) {
        const err = new Error(responseData?.message || "Fout bij het toevoegen van de collectie.");
        err.info = responseData;
        err.status = response.status;
        throw err;
    }

    return responseData;
};

export const updateCollection = async (data) => {
    const isFormData = (typeof FormData !== 'undefined') && (data instanceof FormData);
    const id = isFormData ? data.get('_id') : data._id;
    const response = await fetch(`${BACKEND}/admin/collections/${id}`, {
        method: "PUT",
        headers: isFormData ? {
            Authorization: `Bearer ${getAuthToken()}`,
        } : {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
        },
        body: isFormData ? data : JSON.stringify(data),
    });

    const responseData = await response.json();

    if (!response.ok) {
        const err = new Error(responseData?.message || "Fout bij het bijwerken van de collectie.");
        err.info = responseData;
        err.status = response.status;
        throw err;
    }

    return responseData;
};

export const deleteCollection = async (id) => {
    const response = await fetch(`${BACKEND}/admin/collections/${id}`, {
        method: "DELETE",
        headers: {
            Authorization: `Bearer ${getAuthToken()}`,
        },
    });

    const responseData = await response.json();

    if (!response.ok) {
        const err = new Error(responseData?.message || "Fout bij het verwijderen van de collectie.");
        err.info = responseData;
        err.status = response.status;
        throw err;
    }

    return responseData;
};

export const updateCollectionSortOrder = async (data) => {
    const response = await fetch(`${BACKEND}/admin/collections/order/update`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        const error = new Error("Er is iets misgegaan tijdens het bijwerken van de volgorde.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

// Leveringszone functies
export const fetchDeliveryZones = async ({ signal }) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/delivery-zones`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });

    if (!response.ok) {
        const error = new Error("Er is iets misgegaan tijdens het ophalen van de leveringszones.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const addDeliveryZone = async (data) => {
    const response = await fetch(`${BACKEND}/admin/delivery-zones`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(data),
    });

    const responseData = await response.json();

    if (!response.ok) {
        const err = new Error(responseData?.message || "Fout bij het toevoegen van de leveringszone.");
        err.info = responseData;
        err.status = response.status;
        throw err;
    }

    return responseData;
};

export const updateDeliveryZone = async (data) => {
    const { _id, ...rest } = data;
    const response = await fetch(`${BACKEND}/admin/delivery-zones/${_id}`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(rest),
    });

    const responseData = await response.json();

    if (!response.ok) {
        const err = new Error(responseData?.message || "Fout bij het bijwerken van de leveringszone.");
        err.info = responseData;
        err.status = response.status;
        throw err;
    }

    return responseData;
};

export const fetchDeliverySettings = async ({ signal } = {}) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/delivery-zones/settings`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });

    if (!response.ok) {
        const error = new Error("Er is iets misgegaan tijdens het ophalen van de leveringsinstellingen.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const updateDeliverySettings = async (data) => {
    const response = await fetch(`${BACKEND}/admin/delivery-zones/settings`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(data),
    });

    const responseData = await response.json();

    if (!response.ok) {
        const err = new Error(responseData?.message || "Fout bij het bijwerken van de leveringsinstellingen.");
        err.info = responseData;
        err.status = response.status;
        throw err;
    }

    return responseData;
};

export const deleteDeliveryZone = async (id) => {
    const response = await fetch(`${BACKEND}/admin/delivery-zones/${id}`, {
        method: "DELETE",
        headers: {
            Authorization: `Bearer ${getAuthToken()}`,
        },
    });

    const responseData = await response.json();

    if (!response.ok) {
        const err = new Error(responseData?.message || "Fout bij het verwijderen van de leveringszone.");
        err.info = responseData;
        err.status = response.status;
        throw err;
    }

    return responseData;
};

export const fetchMonthlyRevenue = async ({ signal }) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/orders/revenue/monthly`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });

    if (!response.ok) {
        const error = new Error("Fout bij het ophalen van de maandelijkse omzet.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const fetchRecentOrders = async ({ signal }) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/orders/recent`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });

    if (!response.ok) {
        const error = new Error("Fout bij het ophalen van recente bestellingen.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const createManualOrder = async (orderData) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/orders/manual`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(orderData),
    });

    if (!response.ok) {
        const error = new Error("Fout bij het aanmaken van de handmatige bestelling.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const fetchReservations = async ({ signal, from, to, status }) => {
    const token = getAuthToken();
    const params = new URLSearchParams();
    if (from) params.append("from", from);
    if (to) params.append("to", to);
    if (status) params.append("status", status);

    const response = await fetch(`${BACKEND}/admin/reservations?${params.toString()}`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });

    if (!response.ok) {
        const error = new Error("Fout bij het ophalen van de reservaties.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const createReservationAdmin = async ({
    name,
    email,
    phone,
    date,
    time,
    slotName,
    partySize,
    notes,
    status,
    tableNumbers,
}) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/reservations`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            name,
            email,
            phone,
            date,
            time,
            slotName,
            partySize,
            notes,
            status,
            tableNumbers,
        }),
    });

    if (!response.ok) {
        const error = new Error("Fout bij het aanmaken van de reservatie.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const updateReservationAdmin = async ({
    id,
    name,
    email,
    phone,
    date,
    time,
    slotName,
    partySize,
    notes,
    status,
    tableNumbers,
}) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/reservations/${id}`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            name,
            email,
            phone,
            date,
            time,
            slotName,
            partySize,
            notes,
            status,
            tableNumbers,
        }),
    });

    if (!response.ok) {
        const error = new Error("Fout bij het bijwerken van de reservatie.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const forceReallocateTables = async ({ date, slotName, overrideLocks = true }) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/reservations/reallocate-tables`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ date, slotName, overrideLocks }),
    });

    if (!response.ok) {
        const error = new Error("Fout bij het herschikken van tafels.");
        error.code = response.status;
        error.info = await response.json().catch(() => ({}));
        throw error;
    }

    return response.json();
};

export const updateReservationStatus = async ({ id, status }) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/reservations/${id}/status`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
    });

    if (!response.ok) {
        const error = new Error("Fout bij het bijwerken van de reservatiestatus.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const deleteReservationAdmin = async ({ id }) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/reservations/${id}`, {
        method: "DELETE",
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        const error = new Error("Fout bij het verwijderen van de reservatie.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const updateReservationTables = async ({ id, tableNumbers, tablesLocked }) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/reservations/${id}/tables`, {
        method: "PATCH",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ tableNumbers, tablesLocked }),
    });

    if (!response.ok) {
        const error = new Error("Fout bij het bijwerken van de tafels.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const fetchReservationSettings = async ({ signal }) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/reservations/settings`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });

    if (!response.ok) {
        const error = new Error("Fout bij het ophalen van de reservatie-instellingen.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const updateReservationSettings = async ({
    totalTables,
    isEnabled,
    tables,
    groupLimits,
    maxReservationsPerTimeSlot,
    closures,
    floorPlan,
}) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/reservations/settings`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
            totalTables,
            isEnabled,
            tables,
            groupLimits,
            maxReservationsPerTimeSlot,
            closures,
            floorPlan,
        }),
    });

    if (!response.ok) {
        const error = new Error("Fout bij het bijwerken van de reservatie-instellingen.");
        error.code = response.status;
        error.info = await response.json();
        throw error;
    }

    return response.json();
};

export const importIcalReservations = async (icalFile) => {
    const token = getAuthToken();
    const formData = new FormData();
    formData.append("icalfile", icalFile);

    const response = await fetch(`${BACKEND}/admin/reservations/import-ical`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${token}`,
        },
        body: formData,
    });

    if (!response.ok) {
        const error = new Error("Fout bij importeren van ICAL.");
        error.code = response.status;
        error.info = await response.json().catch(() => ({}));
        throw error;
    }

    return response.json();
};

export const forceImportIcalReservation = async ({ raw, date, time }) => {
    const token = getAuthToken();
    const response = await fetch(`${BACKEND}/admin/reservations/import-ical/force`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ raw, date, time }),
    });

    if (!response.ok) {
        const error = new Error("Fout bij toch importeren van ICAL-item.");
        error.code = response.status;
        error.info = await response.json().catch(() => ({}));
        throw error;
    }

    return response.json();
};

export async function fetchDailyReservationCount() {
    const token = getAuthToken();

    const response = await fetch(`${BACKEND}/admin/reservations/daily-count`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        throw new Error("Failed to fetch daily reservation count.");
    }

    return await response.json();
}

export const fetchReservationAvailabilityAdmin = async ({ date, partySize }) => {
    const token = getAuthToken();
    const params = new URLSearchParams();
    if (date) params.append("date", date);
    if (partySize) params.append("partySize", partySize);

    const response = await fetch(`${BACKEND}/admin/reservations/availability?${params}`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    if (!response.ok) {
        const error = new Error("Fout bij het ophalen van beschikbaarheid.");
        error.code = response.status;
        error.info = await response.json().catch(() => ({}));
        throw error;
    }

    return await response.json();
};

// ---------------------------------------------------------------------------
// Kleine helper voor de nieuwere endpoints: zelfde foutvorm als hierboven
// (error.code = HTTP-status, error.info = JSON-antwoord van de server).
// ---------------------------------------------------------------------------
async function adminRequest(path, { method = "GET", body, signal } = {}, errorMessage = "Er is iets misgegaan.") {
    const headers = { Authorization: `Bearer ${getAuthToken()}` };
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const response = await fetch(`${BACKEND}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal,
    });
    if (!response.ok) {
        const error = new Error(errorMessage);
        error.code = response.status;
        error.info = await response.json().catch(() => ({}));
        throw error;
    }
    return response.json();
}

export const fetchOrderInsights = ({ days = 30, signal } = {}) =>
    adminRequest(`/admin/orders/insights?days=${days}`, { signal }, "Fout bij het ophalen van de cijfers.");

export const lookupGiftCard = (code) =>
    adminRequest(`/admin/gift-cards/lookup/${encodeURIComponent(code)}`, {}, "Cadeaubon niet gevonden.");

export const redeemGiftCard = ({ id, amount, note }) =>
    adminRequest(`/admin/gift-cards/${id}/redeem`, { method: "POST", body: { amount, note } }, "Inwisselen is mislukt.");

// minutes: aantal minuten pauze, 0 = hervatten; untilEndOfDay: tot middernacht
export const setOrderingPause = ({ minutes, untilEndOfDay } = {}) =>
    adminRequest("/admin/account/settings/ordering-pause", { method: "PUT", body: { minutes, untilEndOfDay } }, "Kon online bestellen niet aanpassen.");
